<template>
  <div v-if="show" class="modal-shadow">
    <div class="saved_popup w-11/12 md:w-4/5 lg:w-3/4 xl:w-2/3 mx-auto rounded-lg overflow-hidden bg-white">
      <div class="modal_header flex justify-between items-center px-6 py-4 border-b">
        <h3 class="font-bold">Add location</h3>
        <button @click="close" class="p-2">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="#6B7280" stroke-width="2" stroke-linecap="round"
              stroke-linejoin="round" />
          </svg>
        </button>
      </div>

      <div class="modal_body px-6 py-4">
        <div class="mb-4">
          <div class="search-wrapper relative">
            <input v-model="searchQuery" @input="handleSearch" type="text"
              class="w-full border rounded-md px-4 py-2 pr-10" placeholder="Search an address..." />
            <svg class="absolute right-3 top-1/2" style="transform: translateY(-50%);" width="18" height="18"
              viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M11 18C14.866 18 18 14.866 18 11C18 7.13401 14.866 4 11 4C7.13401 4 4 7.13401 4 11C4 14.866 7.13401 18 11 18Z"
                stroke="#1FBC2F" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M19 19L16 16" stroke="#1FBC2F" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>
          <div v-if="searchQuery && suggestions.length" class="suggestions">
            <ul>
              <li v-for="(s, i) in suggestions" :key="i" @click="selectSuggestion(s)" class="suggestion-item">
                {{ s.address }}
              </li>
            </ul>
          </div>
        </div>

        <div class="map-block">
          <div ref="map" class="map"></div>
        </div>

        <div class="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm text-gray-600 mb-1">Address</label>
            <input type="text" class="w-full border rounded-md px-3 py-2" :value="address" disabled>
          </div>
          <div>
            <label class="block text-sm text-gray-600 mb-1">Name</label>
            <input v-model="name" @input="toggleDisableButton" type="text" class="w-full border rounded-md px-3 py-2"
              placeholder="Give this location a name">
          </div>
        </div>
      </div>

      <div class="btn_line flex justify-end w-full px-6 py-4 border-t modal_footer">
        <button @click="close" class="this_button py-3 px-4 border rounded mr-3">Cancel</button>
        <button @click="save" :disabled="button_disable || !markerPosition"
          :class="button_disable || !markerPosition ? 'text-white bg-gray-400 border-gray-500' : 'text_chameleon bg-green-custom hover:bg-gray-200 border-green-custom'"
          class="this_button py-3 px-4 border rounded">
          Save
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { infoMessage } from "@/services/messages";
import { searchAddressesForModalDebounced } from "@/services/addressSearch";
import { getUserLocation, getMapZoomForLocation, UK_FALLBACK_CENTER } from "@/services/userLocation";

export default {
  name: 'AddLocationModal',
  data() {
    return {
      show: false,
      platform: null,
      map: null,
      ui: null,
      behavior: null,
      geocoder: null,
      marker: null,
      markerPosition: null,
      address: '',
      searchQuery: '',
      suggestions: [],
      name: '',
      button_disable: true,
      user: null,
      userAt: null,
      mapZoom: 6,
    }
  },
  methods: {
    open(locationData = null) {
      console.log('=== Modal.open called ===');
      console.log('locationData:', locationData);

      this.show = true;
      console.log('Modal show set to:', this.show);

      this.$nextTick(async () => {
        console.log('Modal nextTick running');
        const location = await getUserLocation();
        this.userAt = { lat: location.lat, lng: location.lng };
        this.mapZoom = getMapZoomForLocation(location);
        await this.loadHereMapsAPI();
        console.log('HERE Maps API loaded');
        await this.initMap();
        console.log('Map initialized');

        // If location data is provided (from context menu), place marker and set address
        if (locationData && locationData.latitude && locationData.longitude) {
          console.log('Placing marker at:', locationData.latitude, locationData.longitude);
          // Set address first if provided
          if (locationData.address) {
            this.address = locationData.address;
            console.log('Address set to:', this.address);
          }
          // Place marker without triggering reverse geocode if address is already provided
          this.placeOrMoveMarker(locationData.latitude, locationData.longitude, !!locationData.address);
        }
      });
    },
    close() {
      this.resetState();
      this.show = false;
    },
    resetState() {
      this.searchQuery = '';
      this.suggestions = [];
      this.name = '';
      this.address = '';
      this.markerPosition = null;
      if (this.map) {
        this.map.dispose();
        this.map = null;
      }
      this.marker = null;
    },
    async loadHereMapsAPI() {
      return new Promise((resolve, reject) => {
        if (window.H && window.H.ui) {
          return resolve();
        }
        const scripts = [
          'https://js.api.here.com/v3/3.2/mapsjs-core.js',
          'https://js.api.here.com/v3/3.2/mapsjs-service.js',
          'https://js.api.here.com/v3/3.2/mapsjs-ui.js',
          'https://js.api.here.com/v3/3.2/mapsjs-mapevents.js'
        ];
        let loadedCount = 0;
        scripts.forEach(src => {
          const script = document.createElement('script');
          script.src = src;
          script.defer = true;
          script.onload = () => {
            loadedCount++;
            if (loadedCount === scripts.length) {
              resolve();
            }
          };
          script.onerror = reject;
          document.head.appendChild(script);
        });
      });
    },
    async initMap() {
      if (!window.H) {
        console.error('HERE Maps API not loaded');
        return;
      }

      this.platform = new H.service.Platform({
        apikey: process.env.VUE_APP_HERE_API_KEY,
      });
      this.geocoder = this.platform.getSearchService();
      // const engineType = H.Map.EngineType.HARP;
      const defaultLayers = this.platform.createDefaultLayers({ pois: true, traffic: true });
      this.map = new H.Map(
        this.$refs.map,
        defaultLayers.vector.normal.logistics,
        {
          zoom: this.mapZoom,
          center: this.userAt || UK_FALLBACK_CENTER,
          engineType: H.Map.EngineType.HARP
        }
      );
      this.map.addLayer(defaultLayers.vector.traffic.map);

      this.behavior = new H.mapevents.Behavior(new H.mapevents.MapEvents(this.map));
      this.ui = H.ui.UI.createDefault(this.map, defaultLayers);
      this.ui.removeControl('mapsettings');
      const customMapSettings = new H.ui.MapSettingsControl({
        baseLayers: [
          {
            label: 'Standard', layer: [
              defaultLayers.vector.normal.logistics
            ]
          },
          {
            label: 'Satellite', layer: [
              defaultLayers.raster.satellite.map,
            ]
          }
        ],
      });

      this.ui.addControl('mapsettings', customMapSettings);


      // this.map.addLayer(defaultLayers.vector.traffic.logistics);
      const style = this.map.getBaseLayer().getProvider().getStyle();

      const styleChangeHandler = () => {
        if (style.getState() === H.map.render.Style.State.READY) {
          style.removeEventListener('change', styleChangeHandler);

          const enabledFeatures = [
            { feature: 'vehicle restrictions', mode: 'active & inactive' },
            { feature: 'congestion zones', mode: 'all' },
            { feature: 'environmental zones', mode: 'all' }
          ];
          style.setEnabledFeatures(enabledFeatures);
        }
      };
      style.addEventListener('change', styleChangeHandler);

      window.addEventListener('resize', () => this.map.getViewPort().resize());

      // Fit map to UK bounds
      const ukBounds = new H.geo.Rect(58.8, -8.6, 49.9, 1.8);
      this.map.getViewModel().setLookAtData({ bounds: ukBounds });

      const viewport = this.map.getViewPort();
      const map = this.map;

      function zoomToCursor(event, zoomChange) {
        event.stopImmediatePropagation();
        event.preventDefault();

        const rect = viewport.element.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;

        // Capture the geo point under the cursor BEFORE zooming
        const geoUnderCursor = map.screenToGeo(mouseX, mouseY);

        map.setZoom(map.getZoom() + zoomChange, false);

        // After zoom, find where that geo point ended up on screen
        const newScreen = map.geoToScreen(geoUnderCursor);

        // Calculate how much it drifted from the original mouse position
        const driftX = newScreen.x - mouseX;
        const driftY = newScreen.y - mouseY;

        // Shift the center to compensate for the drift
        const currentCenter = map.screenToGeo(
          rect.width / 2 + driftX,
          rect.height / 2 + driftY
        );

        map.setCenter(currentCenter, false);
      }

      viewport.element.addEventListener('wheel', (e) => {
        const delta = e.deltaY < 0 ? 0.5 : -0.5;
        zoomToCursor(e, delta);
      }, { capture: true, passive: false });

      viewport.element.addEventListener('dblclick', (e) => {
        zoomToCursor(e, 2);
      }, { capture: true });

    },
    placeOrMoveMarker(lat, lng, skipReverseGeocode = false) {
      const position = { lat, lng };
      if (!this.marker) {
        const iconSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="42" viewBox="0 0 30 42"><path fill="#1976d2" stroke="#fff" stroke-width="2" d="M15,40 C15,40 2,24 2,15 C2,8 8,2 15,2 C22,2 28,8 28,15 C28,24 15,40 15,40 Z" /></svg>`;
        const icon = new H.map.Icon('data:image/svg+xml;charset=utf-8,' + encodeURIComponent(iconSvg), { anchor: { x: 15, y: 40 } });
        this.marker = new H.map.Marker(position, { icon });
        this.marker.draggable = true;
        this.marker.addEventListener('dragstart', () => {
          if (this.behavior) this.behavior.disable(H.mapevents.Behavior.DRAGGING);
        }, false);
        this.marker.addEventListener('drag', (ev) => {
          const p = ev.currentPointer;
          const newPos = this.map.screenToGeo(p.viewportX, p.viewportY);
          ev.target.setGeometry(newPos);
        }, false);
        this.marker.addEventListener('dragend', (e) => {
          if (this.behavior) this.behavior.enable(H.mapevents.Behavior.DRAGGING);
          const pos = e.target.getGeometry();
          this.markerPosition = { lat: pos.lat, lng: pos.lng };
          this.reverseGeocode(pos.lat, pos.lng);
        }, false);
        this.map.addObject(this.marker);
      } else {
        this.marker.setGeometry(position);
      }
      this.markerPosition = { lat, lng };
      this.map.getViewModel().setLookAtData({ position, zoom: 16 }, true);

      // Only reverse geocode if not skipped (i.e., when address is not already provided)
      if (!skipReverseGeocode) {
        this.reverseGeocode(lat, lng);
      }
    },
    reverseGeocode(lat, lng) {
      if (!this.geocoder) return;
      this.geocoder.reverseGeocode(
        { at: `${lat},${lng}` },
        (result) => {
          if (result.items && result.items.length) {
            this.address = result.items[0].address.label;
          }
        },
        (err) => {
          console.error('Reverse geocode error', err);
        }
      );
    },
    async handleSearch() {
      if (!this.searchQuery || !this.searchQuery.trim()) {
        this.suggestions = [];
        return;
      }
      const user = JSON.parse(localStorage.getItem('user')) || null;
      this.suggestions = await searchAddressesForModalDebounced(
        this.$http,
        this.searchQuery,
        user ? user.id : null,
        'add-location',
        this.userAt,
      );
    },
    selectSuggestion(s) {
      this.searchQuery = s.address;
      this.suggestions = [];
      this.placeOrMoveMarker(s.position.lat, s.position.lng);
    },
    toggleDisableButton() {
      this.button_disable = !(this.name && this.name.length);
    },
    async save() {
      if (!this.markerPosition || !this.name) return;
      try {
        this.user = JSON.parse(localStorage.getItem('user')) || null;
        const payload = {
          name: this.name,
          user_id: this.user?.id,
          address: this.address || this.searchQuery,
          postcode: null,
          geo: { latitude: this.markerPosition.lat, longitude: this.markerPosition.lng },
        };
        const resp = await this.$http.postAuth(`${this.$http.apiUrl()}/locations/create`, payload);
        if (resp && resp.data) {
          infoMessage('Location created', 'success');
          this.$emit('created');
          this.close();
        }
      } catch (e) {
        console.error(e);
        infoMessage('Oops. Something went wrong...', 'error');
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.modal-shadow {
  position: fixed;
  top: 0;
  left: 0;
  min-height: 100vh;
  width: 100vw;
  background: rgba(0, 0, 0, 0.39);
  z-index: 1000;
}

.modal_header {
  border-radius: 8px 8px 0 0;
}

.modal_footer {
  border-radius: 0 0 8px 8px;
}

.map-block {
  height: 50vh;
}

.map {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.suggestions {
  border: 1px solid #D0D5DA;
  border-radius: 6px;
  margin-top: 6px;
  max-height: 240px;
  overflow-y: auto;
}

.suggestions ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.suggestion-item {
  padding: 10px 12px;
  cursor: pointer;
}

.suggestion-item:hover {
  background: #f5f5f5;
}
</style>
